import { MoreHoriz, TransferWithinAStation } from '@mui/icons-material';
import { Grid } from '@mui/material';
import { dateFormat, dateFormatLongDate } from '@nx-smartmonkey/shared/helpers';
import { HSpacer, RoutalAvatar, RoutalLoader, RoutalPalette } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import CountryIcon from '../../../../../assets/img/backoffice/country.svg';
import CreatedAtIcon from '../../../../../assets/img/backoffice/created_at.svg';
import EmailIcon from '../../../../../assets/img/backoffice/email.svg';
import LicenseIcon from '../../../../../assets/img/backoffice/license.svg';
import PaymentDateIcon from '../../../../../assets/img/backoffice/payment_date.svg';
import PaymentStatusIcon from '../../../../../assets/img/backoffice/payment_status.svg';
import GeocodeCreditIcon from '../../../../../assets/img/backoffice/stop.svg';
import VehicleIcon from '../../../../../assets/img/backoffice/vehicle.svg';
import { getCountryLabelByCountryCode } from '../../../../../constants/Countries';
import * as routing from '../../../../../constants/Routing';
import { ProjectBase } from '../../../../../domain/backoffice/project/ProjectBase';
import { UserBase } from '../../../../../domain/backoffice/user/UserBase';
import { intl } from '../../../../../helpers/IntGlobalProvider';
import { useAsUser } from '../../../../../hooks/session/useAsUser';
import { useRetrieveAdminUser } from '../../../../../hooks/user/useRetrieveAdminUser';
import { useRetrieveUser as useRetrieveLoggedUser } from '../../../../../hooks/user/useRetrieveUser';
import Button from '../../../../../react-components/Button';
import Text from '../../../../../react-components/Text';
import { DataTable } from '../../../../../react-components/data-table/DataTable';
import { getUploadAsBase64 } from '../../../../../services/requests';
import { useRetrieveOrganization } from '../../../hooks/organizations/useRetrieveOrganization';
import { InfoBox, InfoBoxItem, InfoBoxText } from '../../../shared/InfoBox';
import { SystemsInfoBox } from '../../../shared/SystemsInfoBox';
import { getProjectsTableColumns } from '../../../shared/detailsProjectsTableColumns';
import { getUsersTableColumns } from '../../../shared/detailsUsersTableColumns';
import { getPaymentStatusColor } from '../list/BackofficeClientsOrganizationsListColumns';
import { BackofficeClientsOrganizationActionsMenu } from '../shared/BackofficeClientsOrganizationActionsMenu';

export const BackofficeClientsOrganizationsDetails = () => {
  const { beAsUser } = useAsUser();
  const { data: adminUser } = useRetrieveAdminUser();
  const { data: loggedUser } = useRetrieveLoggedUser();
  const navigate = useNavigate();
  const { organizationId } = useParams();
  const {
    data: organization,
    isLoading,
    error,
    run: refetchOrganization,
  } = useRetrieveOrganization({ organizationId });
  const [hideDeletedProjects, setHideDeletedProjects] = useState(false);
  const [hideDeletedUsers, setHideDeletedUsers] = useState(false);

  useEffect(() => {
    if (error) {
      navigate(routing.BACKOFFICE.CLIENTS.ORGANIZATIONS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  if (isLoading) {
    return (
      <Grid
        item
        container
        direction="column"
        style={{ height: `100%`, width: `100%` }}
        alignItems="center"
        justifyContent="center"
      >
        <RoutalLoader />
      </Grid>
    );
  }

  if (!organization) return null;

  const visibleProjects = !hideDeletedProjects
    ? organization.projects
    : organization.projects.filter((project: ProjectBase) => !project.deleted);

  const visibleUsers = !hideDeletedUsers
    ? organization.users
    : organization.users.filter((user: UserBase) => !user.deleted);

  return (
    <Grid
      container
      direction="column"
      wrap="nowrap"
      style={{ backgroundColor: `white`, zIndex: 2, height: `100%`, overflowY: `auto` }}
    >
      <Grid
        style={{ padding: `16px 22px`, borderBottom: `1px solid #e0e0e0` }}
        container
        item
        justifyContent="space-between"
        direction="row"
        wrap="nowrap"
      >
        <Grid item container direction="row" alignItems="center">
          <RoutalAvatar
            large
            url={organization.avatar_url}
            getUploadAsBase64={getUploadAsBase64}
            label={organization.label}
          />

          <HSpacer medium />

          <Text variant="body-large">{organization.label}</Text>
        </Grid>

        <Grid item container direction="row" alignItems="center" justifyContent="flex-end">
          {organization.id !== adminUser?.id && organization.id !== loggedUser?.id ? (
            <>
              <Button
                styleType="secondary"
                variant="flat"
                onClick={() => {
                  beAsUser({ id: organization.owner_id });
                  window.open(routing.PLANNER.ROOT, `_blank`);
                }}
                style={{ height: `28px` }}
              >
                <TransferWithinAStation />
              </Button>

              <HSpacer small />
            </>
          ) : null}

          <BackofficeClientsOrganizationActionsMenu
            organization={organization}
            type="popover"
            popoverProps={{
              hideArrow: true,
              content: <MoreHoriz />,
              containerStyle: {
                width: `unset`,
              },
            }}
            onManageSMS={() => refetchOrganization()}
            onManageWhatsApp={() => refetchOrganization()}
            onManageGeocodeCredit={() => refetchOrganization()}
            onManageContractUpdate={() => refetchOrganization()}
          />
        </Grid>
      </Grid>

      <div style={{ padding: `16px 22px`, overflow: `auto` }}>
        <Grid container direction="column" spacing={2}>
          <Grid item style={{ maxWidth: `500px` }}>
            <SystemsInfoBox
              email={organization.users.find((u) => u.id === organization.owner_id)?.email}
              subscription={organization.subscription}
            />
          </Grid>

          <Grid item style={{ maxWidth: `500px` }}>
            <InfoBox title={intl.formatMessage({ id: `backoffice.information` })}>
              <div style={{ height: `calc(100% - 32px)`, width: `calc(100% - 16px)`, padding: `0 8px` }}>
                <InfoBoxItem
                  label={intl.formatMessage({ id: `backoffice.clients.organizations.country` })}
                  icon={
                    <img
                      src={CountryIcon}
                      style={{ marginLeft: `2px`, width: `14px`, marginRight: `2px` }}
                      alt=""
                      draggable="false"
                    />
                  }
                >
                  <InfoBoxText>
                    {getCountryLabelByCountryCode({
                      countryCode: organization.country_code?.toUpperCase(),
                      userLanguage: adminUser?.language ?? loggedUser?.language,
                    }) ?? `-`}
                  </InfoBoxText>
                </InfoBoxItem>

                <InfoBoxItem
                  label={intl.formatMessage({ id: `backoffice.clients.organizations.created_at` })}
                  icon={<img src={CreatedAtIcon} style={{ width: `18px` }} alt="" draggable="false" />}
                >
                  <InfoBoxText>{dateFormat(organization.created_at)}</InfoBoxText>
                </InfoBoxItem>

                <InfoBoxItem
                  label={intl.formatMessage({ id: `backoffice.clients.organizations.subscription_plan` })}
                  icon={<img src={LicenseIcon} style={{ width: `18px` }} alt="" draggable="false" />}
                >
                  <InfoBoxText>
                    <span style={{ textTransform: `capitalize`, marginRight: `4px` }}>
                      {organization.subscription.plan}
                    </span>
                  </InfoBoxText>
                </InfoBoxItem>

                <InfoBoxItem
                  label={intl.formatMessage({ id: `backoffice.clients.organizations.contract_enabled` })}
                  icon={<img src={LicenseIcon} style={{ width: `18px` }} alt="" draggable="false" />}
                >
                  <InfoBoxText>
                    {intl.formatMessage({ id: organization.subscription.has_contract ? `enabled` : `disabled` })}
                  </InfoBoxText>
                </InfoBoxItem>

                {organization.subscription.has_contract && (
                  <Grid
                    style={{ padding: `10px`, backgroundColor: `${RoutalPalette.neutral02}77`, borderRadius: `5px` }}
                  >
                    <Text
                      variant="body"
                      intlId="backoffice.clients.organizations.contract_info.title"
                      style={{ color: RoutalPalette.neutral20 }}
                    />

                    <Grid paddingLeft={3}>
                      <InfoBoxItem
                        label={intl.formatMessage({ id: `backoffice.clients.organizations.contract_info.vehicles` })}
                      >
                        <InfoBoxText>
                          <span>{organization.subscription.vehicles}</span>
                        </InfoBoxText>
                      </InfoBoxItem>
                      <InfoBoxItem
                        label={intl.formatMessage({ id: `backoffice.clients.organizations.contract_info.projects` })}
                      >
                        <InfoBoxText>
                          <span>{organization.subscription.projects}</span>
                        </InfoBoxText>
                      </InfoBoxItem>
                      <InfoBoxItem
                        label={intl.formatMessage({ id: `backoffice.clients.organizations.contract_info.users` })}
                      >
                        <InfoBoxText>
                          <span>{organization.subscription.users}</span>
                        </InfoBoxText>
                      </InfoBoxItem>

                      <InfoBoxItem
                        label={intl.formatMessage({ id: `backoffice.clients.organizations.contract_info.supervisors` })}
                      >
                        <InfoBoxText>
                          <span>{organization.subscription.supervisors}</span>
                        </InfoBoxText>
                      </InfoBoxItem>
                    </Grid>
                  </Grid>
                )}

                <InfoBoxItem
                  label={intl.formatMessage({ id: `backoffice.clients.organizations.subscription_licenses` })}
                  icon={<img src={VehicleIcon} style={{ width: `18px` }} alt="" draggable="false" />}
                >
                  <InfoBoxText>
                    <span>{organization.subscription.vehicles}</span>
                  </InfoBoxText>
                </InfoBoxItem>

                <InfoBoxItem
                  label={intl.formatMessage({ id: `backoffice.clients.organizations.emails_usage` })}
                  icon={<img src={EmailIcon} style={{ width: `18px` }} alt="" draggable="false" />}
                >
                  <InfoBoxText>{`${
                    organization.subscription.emails.period_usage
                  } / ${organization.subscription.getSubscriptionMaxCustomerEmails()}`}</InfoBoxText>
                </InfoBoxItem>

                <InfoBoxItem
                  label={intl.formatMessage({ id: `backoffice.clients.organizations.geocode_credit` })}
                  icon={<img src={GeocodeCreditIcon} style={{ width: `18px` }} alt="" draggable="false" />}
                >
                  <InfoBoxText>{organization.subscription.geocode_credit}</InfoBoxText>
                </InfoBoxItem>

                {/* TODO: paradas creadas */}

                <InfoBoxItem
                  label={intl.formatMessage({ id: `backoffice.clients.organizations.payment_status` })}
                  icon={<img src={PaymentStatusIcon} style={{ width: `18px` }} alt="" draggable="false" />}
                >
                  <InfoBoxText
                    style={{
                      color: getPaymentStatusColor(organization.subscription.status),
                      textTransform: `uppercase`,
                    }}
                  >
                    {organization.subscription.status}
                  </InfoBoxText>
                </InfoBoxItem>

                <InfoBoxItem
                  label={intl.formatMessage({ id: `backoffice.clients.organizations.payment_date` })}
                  icon={<img src={PaymentDateIcon} style={{ width: `18px` }} alt="" draggable="false" />}
                >
                  <InfoBoxText>{dateFormatLongDate(organization.subscription.current_period_end)}</InfoBoxText>
                </InfoBoxItem>
              </div>
            </InfoBox>
          </Grid>

          <Grid item style={{ maxWidth: `1500px` }}>
            <InfoBox
              title={intl.formatMessage({ id: `backoffice.clients.users_num` }, { num: visibleUsers.length })}
              showText={intl.formatMessage({ id: `backoffice.shared.info_box.show` })}
              hideText={intl.formatMessage({ id: `backoffice.shared.info_box.hide` })}
              hide={hideDeletedUsers}
              onHide={(status) => setHideDeletedUsers(status)}
            >
              <DataTable
                loading={isLoading}
                showCheckboxes={false}
                docs={visibleUsers.map((user: UserBase) => ({ doc: user }))}
                noFooter
                hover
                total={visibleUsers.length}
                columns={getUsersTableColumns({
                  navigate: (destinationUrl: string) => navigate(destinationUrl),
                })}
              />
            </InfoBox>
          </Grid>

          <Grid item style={{ maxWidth: `1500px` }}>
            <InfoBox
              title={intl.formatMessage({ id: `backoffice.clients.projects_num` }, { num: visibleProjects.length })}
              showText={intl.formatMessage({ id: `backoffice.shared.info_box.show` })}
              hideText={intl.formatMessage({ id: `backoffice.shared.info_box.hide` })}
              hide={hideDeletedProjects}
              onHide={(status) => setHideDeletedProjects(status)}
            >
              <DataTable
                loading={isLoading}
                showCheckboxes={false}
                docs={visibleProjects.map((project: ProjectBase) => ({ doc: project }))}
                noFooter
                hover
                total={visibleProjects.length}
                columns={getProjectsTableColumns({
                  defaultProjectId: organization.default_project_id,
                  navigate: (destinationUrl: string) => navigate(destinationUrl),
                  onRefreshContent: () => refetchOrganization(),
                })}
              />
            </InfoBox>
          </Grid>
        </Grid>
      </div>
    </Grid>
  );
};
